import { useState, useMemo } from "react";
import { useCards } from "@/pages/Dashboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CheckCircle, Clock, Upload, FileText, Plus, Archive, RotateCcw, Trash2, ChevronLeft, MessageSquare, Tag, Calendar, Milestone, Pencil, Save, X } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const statusColors = {
  "Not Started": "bg-slate-100 text-slate-600",
  "In Progress": "bg-blue-100 text-blue-700",
  "Testing": "bg-amber-100 text-amber-700",
  "Done": "bg-emerald-100 text-emerald-700",
  "Blocked": "bg-red-100 text-red-700"
};

const statusIcons = {
  "Not Started": <Clock className="w-3 h-3" />,
  "In Progress": <Clock className="w-3 h-3" />,
  "Testing": <Clock className="w-3 h-3" />,
  "Done": <CheckCircle className="w-3 h-3" />,
  "Blocked": <AlertTriangle className="w-3 h-3" />
};

function getComponentGroup(layerKey) {
  const colonIdx = layerKey.indexOf(": ");
  return colonIdx > -1 ? layerKey.substring(0, colonIdx) : layerKey;
}
function getBaseComponent(groupName) {
  const bracketIdx = groupName.indexOf(" [");
  return bracketIdx > -1 ? groupName.substring(0, bracketIdx) : groupName;
}
function getConfigName(groupName) {
  const match = groupName.match(/\[(.+?)\]/);
  return match ? match[1] : null;
}
function getLayerName(layerKey) {
  const colonIdx = layerKey.indexOf(": ");
  return colonIdx > -1 ? layerKey.substring(colonIdx + 2) : layerKey;
}

export default function CardDetailModal({ card, initialComponent, onClose }) {
  const { config, updateLayerStatus, addNote, uploadFile, deleteFile, fetchData, authHeaders, token } = useCards();
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [editingLayer, setEditingLayer] = useState(null);
  const [layerEdit, setLayerEdit] = useState({});
  const [activeComponent, setActiveComponent] = useState(initialComponent || null);
  const [layerNoteInput, setLayerNoteInput] = useState({});
  const [addingLayerNote, setAddingLayerNote] = useState(null);
  const [expandedLayerNotes, setExpandedLayerNotes] = useState({});
  const [expandedMilestones, setExpandedMilestones] = useState({});
  const [milestoneForm, setMilestoneForm] = useState({});
  const [editingCard, setEditingCard] = useState(false);
  const [cardEdit, setCardEdit] = useState({});

  const layerStatuses = card.layer_statuses || {};
  const historyNotes = card.history_notes || [];
  const attachments = card.attachments || [];
  const hwConfigs = card.hardware_configs || [];

  const componentGroups = useMemo(() => {
    const groups = new Set();
    Object.keys(layerStatuses).forEach(key => groups.add(getComponentGroup(key)));
    return Array.from(groups);
  }, [layerStatuses]);

  const baseComponents = useMemo(() => {
    const bases = new Set();
    componentGroups.forEach(g => bases.add(getBaseComponent(g)));
    return Array.from(bases);
  }, [componentGroups]);

  const groupedLayers = useMemo(() => {
    const groups = {};
    Object.entries(layerStatuses).forEach(([key, data]) => {
      const group = getComponentGroup(key);
      if (!groups[group]) groups[group] = [];
      groups[group].push({ key, ...data });
    });
    return groups;
  }, [layerStatuses]);

  const groupProgress = useMemo(() => {
    const progress = {};
    Object.entries(groupedLayers).forEach(([group, layers]) => {
      const done = layers.filter(l => l.status === "Done").length;
      const blocked = layers.filter(l => l.status === "Blocked").length;
      const testing = layers.filter(l => l.status === "Testing").length;
      const total = layers.length;
      progress[group] = { done, blocked, testing, total, percent: total > 0 ? Math.round((done / total) * 100) : 0 };
    });
    return progress;
  }, [groupedLayers]);

  const displayedGroups = useMemo(() => {
    if (!activeComponent) return groupedLayers;
    const filtered = {};
    Object.entries(groupedLayers).forEach(([group, layers]) => {
      if (getBaseComponent(group) === activeComponent) filtered[group] = layers;
    });
    return filtered;
  }, [activeComponent, groupedLayers]);

  // Compute earliest/latest dates across all layers
  const timelineSummary = useMemo(() => {
    let earliest = null, latest = null;
    Object.values(layerStatuses).forEach(l => {
      if (l.start_date && (!earliest || l.start_date < earliest)) earliest = l.start_date;
      if (l.end_date && (!latest || l.end_date > latest)) latest = l.end_date;
    });
    return { earliest, latest };
  }, [layerStatuses]);

  const handleLayerEdit = (layerKey, data) => {
    setEditingLayer(layerKey);
    setLayerEdit({
      status: data.status || "Not Started",
      blocker_note: data.blocker_note || "",
      version: data.version || "",
      version_detail: data.version_detail || "",
      ownership: data.ownership || "Internal",
      start_date: data.start_date || "",
      end_date: data.end_date || ""
    });
  };

  const saveLayerEdit = async () => {
    if (!editingLayer) return;
    try {
      await axios.put(`${API}/plm/cards/${card.id}/layer`, {
        layer: editingLayer, ...layerEdit,
        start_date: layerEdit.start_date || null,
        end_date: layerEdit.end_date || null
      }, { headers: authHeaders });
      await fetchData();
      toast.success("Layer updated");
      setEditingLayer(null);
    } catch { toast.error("Failed to update layer"); }
  };

  const handleCardEdit = () => {
    setEditingCard(true);
    setCardEdit({
      card_title: card.card_title,
      description: card.description || "",
      project_client: card.project_client,
      lifecycle_stage: card.lifecycle_stage,
      team_owner: card.team_owner,
      vertical: card.vertical,
      sku: card.sku,
      card_type: card.card_type
    });
  };

  const saveCardEdit = async () => {
    try {
      await axios.put(`${API}/plm/cards/${card.id}`, cardEdit, { headers: authHeaders });
      await fetchData();
      toast.success("Card updated");
      setEditingCard(false);
    } catch { toast.error("Failed to update card"); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    const success = await addNote(card.id, newNote.trim());
    if (success) setNewNote("");
    setAddingNote(false);
  };

  const handleAddLayerNote = async (layerKey) => {
    const text = layerNoteInput[layerKey]?.trim();
    if (!text) return;
    setAddingLayerNote(layerKey);
    try {
      await axios.post(`${API}/plm/cards/${card.id}/layer-notes`, { layer: layerKey, note: text }, { headers: authHeaders });
      await fetchData();
      setLayerNoteInput(prev => ({ ...prev, [layerKey]: "" }));
      toast.success("Layer note added");
    } catch { toast.error("Failed to add layer note"); }
    setAddingLayerNote(null);
  };

  const handleAddMilestone = async (layerKey) => {
    const form = milestoneForm[layerKey];
    if (!form?.title?.trim()) { toast.error("Title required"); return; }
    try {
      await axios.post(`${API}/plm/cards/${card.id}/milestones`, {
        layer: layerKey, title: form.title, start_date: form.start_date || null,
        end_date: form.end_date || null, notes: form.notes || ""
      }, { headers: authHeaders });
      await fetchData();
      setMilestoneForm(prev => ({ ...prev, [layerKey]: null }));
      toast.success("Milestone added");
    } catch { toast.error("Failed to add milestone"); }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    try {
      await axios.delete(`${API}/plm/cards/${card.id}/milestones/${milestoneId}`, { headers: authHeaders });
      await fetchData();
      toast.success("Milestone deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) { await uploadFile(card.id, file); e.target.value = ""; }
  };

  const handleArchive = async () => {
    try {
      await axios.post(`${API}/plm/cards/${card.id}/archive`, {}, { headers: authHeaders });
      toast.success("Card archived"); fetchData(); onClose();
    } catch { toast.error("Failed to archive"); }
  };

  const handleUnarchive = async () => {
    try {
      await axios.post(`${API}/plm/cards/${card.id}/unarchive`, {}, { headers: authHeaders });
      toast.success("Card unarchived"); fetchData(); onClose();
    } catch { toast.error("Failed to unarchive"); }
  };

  const ownershipOptions = config?.ownership_options || ["Internal", "Partner", "Vendor"];

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="card-detail-modal" aria-describedby="card-detail-desc">
        <DialogHeader className="flex-shrink-0">
          {editingCard ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={cardEdit.vertical} onValueChange={(v) => setCardEdit(p => ({ ...p, vertical: v }))}>
                  <SelectTrigger className="h-7 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{config?.verticals?.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={cardEdit.sku} onChange={(e) => setCardEdit(p => ({ ...p, sku: e.target.value }))} className="h-7 text-xs w-[100px]" placeholder="SKU" />
                <Select value={cardEdit.card_type} onValueChange={(v) => setCardEdit(p => ({ ...p, card_type: v }))}>
                  <SelectTrigger className="h-7 w-[150px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{config?.card_types?.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={cardEdit.lifecycle_stage} onValueChange={(v) => setCardEdit(p => ({ ...p, lifecycle_stage: v }))}>
                  <SelectTrigger className="h-7 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{config?.lifecycle_stages?.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={cardEdit.team_owner} onValueChange={(v) => setCardEdit(p => ({ ...p, team_owner: v }))}>
                  <SelectTrigger className="h-7 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{config?.teams?.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input value={cardEdit.card_title} onChange={(e) => setCardEdit(p => ({ ...p, card_title: e.target.value }))} className="text-sm" placeholder="Card Title" />
              <Input value={cardEdit.project_client} onChange={(e) => setCardEdit(p => ({ ...p, project_client: e.target.value }))} className="h-7 text-xs" placeholder="Client" />
              <Textarea value={cardEdit.description} onChange={(e) => setCardEdit(p => ({ ...p, description: e.target.value }))} className="text-xs min-h-[60px]" placeholder="Description..." />
              <div className="flex gap-2">
                <Button size="sm" className="h-7" onClick={saveCardEdit} data-testid="save-card-edit"><Save className="w-3 h-3 mr-1" />Save</Button>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditingCard(false)}><X className="w-3 h-3 mr-1" />Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono text-sm font-bold bg-slate-100 px-2 py-0.5 rounded">{card.sku}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100">{card.vertical}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100">{card.lifecycle_stage}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${card.overall_status === "Completed" ? "bg-emerald-100 text-emerald-700" : card.overall_status === "Blocked" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{card.overall_status}</span>
                  {hwConfigs.length > 0 && <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">Multi-Config: {hwConfigs.map(h => h.name).join(" + ")}</span>}
                </div>
                <DialogTitle className="text-lg">{card.card_title}</DialogTitle>
                <p id="card-detail-desc" className="text-sm text-slate-500">{card.project_client} | {card.card_type} | {card.team_owner}</p>
                {card.description && <p className="text-xs text-slate-500 mt-1 max-w-2xl">{card.description}</p>}
                {timelineSummary.earliest && (
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {timelineSummary.earliest} - {timelineSummary.latest || 'TBD'}</p>
                )}
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <Button size="sm" variant="outline" className="h-7" onClick={handleCardEdit} data-testid="edit-card-btn"><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                {card.is_archived ? (
                  <Button size="sm" variant="outline" className="h-7" onClick={handleUnarchive} data-testid="unarchive-btn"><RotateCcw className="w-3 h-3 mr-1" />Unarchive</Button>
                ) : (
                  <Button size="sm" variant="outline" className="h-7" onClick={handleArchive} data-testid="archive-btn"><Archive className="w-3 h-3 mr-1" />Archive</Button>
                )}
              </div>
            </div>
          )}
        </DialogHeader>

        <Tabs defaultValue="layers" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
            <TabsTrigger value="layers" data-testid="tab-layers">Components ({Object.keys(layerStatuses).length})</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">History ({historyNotes.length})</TabsTrigger>
            <TabsTrigger value="files" data-testid="tab-files">Files ({attachments.length})</TabsTrigger>
          </TabsList>

          {/* ===== LAYERS TAB ===== */}
          <TabsContent value="layers" className="flex-1 overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 mb-3 flex-wrap flex-shrink-0">
              <span className="text-xs text-slate-500">Filter:</span>
              <button onClick={() => setActiveComponent(null)} className={`text-xs px-2 py-1 rounded border transition-colors ${!activeComponent ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`} data-testid="filter-all-components">All</button>
              {baseComponents.map(comp => (
                <button key={comp} onClick={() => setActiveComponent(activeComponent === comp ? null : comp)}
                  className={`text-xs px-2 py-1 rounded border transition-colors ${activeComponent === comp ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                  data-testid={`filter-component-${comp.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >{comp}</button>
              ))}
            </div>
            {activeComponent && <button onClick={() => setActiveComponent(null)} className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-2"><ChevronLeft className="w-4 h-4" /> All components</button>}

            <ScrollArea className="flex-1">
              <div className="space-y-4 pr-4">
                {Object.entries(displayedGroups).map(([group, layers]) => {
                  const prog = groupProgress[group];
                  const cfgName = getConfigName(group);
                  const baseName = getBaseComponent(group);
                  return (
                    <div key={group} className="border rounded p-3" data-testid={`component-group-${group.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-sm text-slate-900">{baseName}</h4>
                          {cfgName && <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">{cfgName}</span>}
                          {prog.blocked > 0 && <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded">{prog.blocked} blocked</span>}
                          {prog.testing > 0 && <span className="text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded">{prog.testing} testing</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-1.5 bg-slate-200 rounded overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${prog.percent}%` }}></div></div>
                          <span className="text-xs text-slate-500">{prog.percent}%</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {layers.map(({ key, status, blocker_note, version, version_detail, ownership, start_date, end_date, milestones, notes }) => {
                          const layerNotes = notes || [];
                          const layerMilestones = milestones || [];
                          const isNotesExpanded = expandedLayerNotes[key];
                          const isMilestonesExpanded = expandedMilestones[key];
                          const mForm = milestoneForm[key];

                          return (
                            <div key={key} className="border-b border-slate-100 last:border-0 pb-2 last:pb-0">
                              {editingLayer === key ? (
                                <div className="space-y-2 py-1.5">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm text-slate-600 font-medium">{getLayerName(key)}</span>
                                    <Select value={layerEdit.status} onValueChange={(v) => setLayerEdit(p => ({ ...p, status: v }))}>
                                      <SelectTrigger className="h-7 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                                      <SelectContent>{config?.statuses?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <Select value={layerEdit.ownership} onValueChange={(v) => setLayerEdit(p => ({ ...p, ownership: v }))}>
                                      <SelectTrigger className="h-7 w-[100px] text-xs"><SelectValue /></SelectTrigger>
                                      <SelectContent>{ownershipOptions.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <Input value={layerEdit.blocker_note} onChange={(e) => setLayerEdit(p => ({ ...p, blocker_note: e.target.value }))} placeholder="Blocker note" className="h-7 text-xs flex-1 min-w-[120px]" />
                                  </div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Tag className="w-3 h-3 text-slate-400" />
                                    <Input value={layerEdit.version} onChange={(e) => setLayerEdit(p => ({ ...p, version: e.target.value }))} placeholder="Version" className="h-7 text-xs w-[100px]" />
                                    <Input value={layerEdit.version_detail} onChange={(e) => setLayerEdit(p => ({ ...p, version_detail: e.target.value }))} placeholder="Version detail" className="h-7 text-xs flex-1 min-w-[120px]" />
                                  </div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Calendar className="w-3 h-3 text-slate-400" />
                                    <Input type="date" value={layerEdit.start_date || ""} onChange={(e) => setLayerEdit(p => ({ ...p, start_date: e.target.value }))} className="h-7 text-xs w-[130px]" />
                                    <span className="text-xs text-slate-400">to</span>
                                    <Input type="date" value={layerEdit.end_date || ""} onChange={(e) => setLayerEdit(p => ({ ...p, end_date: e.target.value }))} className="h-7 text-xs w-[130px]" />
                                    <Button size="sm" className="h-7 px-3" onClick={saveLayerEdit} data-testid={`save-layer-${key}`}>Save</Button>
                                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingLayer(null)}>Cancel</Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="py-1.5">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <span className="text-sm text-slate-700">{getLayerName(key)}</span>
                                      {ownership && ownership !== "Internal" && <span className="text-[9px] font-medium px-1 py-0.5 rounded bg-orange-100 text-orange-700">{ownership}</span>}
                                      {version && <span className="text-[10px] font-mono bg-slate-100 px-1 py-0.5 rounded text-slate-600">{version}</span>}
                                      {version_detail && <span className="text-[10px] text-slate-400 truncate max-w-[120px]" title={version_detail}>{version_detail}</span>}
                                      {(start_date || end_date) && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" />{start_date || '?'} - {end_date || '?'}</span>}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {blocker_note && status === "Blocked" && <span className="text-xs text-red-500 max-w-[120px] truncate" title={blocker_note}>{blocker_note}</span>}
                                      {layerMilestones.length > 0 && (
                                        <button onClick={() => setExpandedMilestones(p => ({ ...p, [key]: !p[key] }))} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-0.5">
                                          <Milestone className="w-3 h-3" />{layerMilestones.length}
                                        </button>
                                      )}
                                      {layerNotes.length > 0 && (
                                        <button onClick={() => setExpandedLayerNotes(p => ({ ...p, [key]: !p[key] }))} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-0.5" data-testid={`layer-notes-toggle-${key}`}>
                                          <MessageSquare className="w-3 h-3" />{layerNotes.length}
                                        </button>
                                      )}
                                      <button onClick={() => handleLayerEdit(key, { status, blocker_note, version, version_detail, ownership, start_date, end_date })}
                                        className={`text-xs px-2 py-1 rounded flex items-center gap-1 hover:opacity-80 ${statusColors[status]}`}
                                        data-testid={`layer-status-${key}`}
                                      >{statusIcons[status]} {status}</button>
                                    </div>
                                  </div>

                                  {/* Milestones expanded */}
                                  {isMilestonesExpanded && (
                                    <div className="mt-2 ml-4 space-y-1 bg-amber-50 rounded p-2 border border-amber-100">
                                      <p className="text-[10px] font-semibold text-amber-700 mb-1">Milestones</p>
                                      {layerMilestones.map(m => (
                                        <div key={m.id} className="flex items-center justify-between text-xs text-slate-600 bg-white rounded px-2 py-1">
                                          <div className="flex-1">
                                            <span className="font-medium">{m.title}</span>
                                            {m.start_date && <span className="text-slate-400 ml-2">{m.start_date} - {m.end_date || '?'}</span>}
                                            {m.notes && <p className="text-[10px] text-slate-400">{m.notes}</p>}
                                          </div>
                                          <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-400" onClick={() => handleDeleteMilestone(m.id)}><Trash2 className="w-3 h-3" /></Button>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Notes expanded */}
                                  {isNotesExpanded && (
                                    <div className="mt-2 ml-4 space-y-1 bg-slate-50 rounded p-2">
                                      {layerNotes.map((n, i) => (
                                        <div key={n.id || i} className="text-xs text-slate-600">
                                          <span className="text-slate-800">{n.text}</span>
                                          <span className="text-slate-400 ml-2">{n.user} | {new Date(n.timestamp).toLocaleString()}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Add note + milestone inline */}
                                  <div className="mt-1 flex items-center gap-1">
                                    <Input value={layerNoteInput[key] || ""} onChange={(e) => setLayerNoteInput(prev => ({ ...prev, [key]: e.target.value }))}
                                      placeholder="Add note..." className="h-6 text-xs flex-1"
                                      onKeyDown={(e) => e.key === "Enter" && handleAddLayerNote(key)} data-testid={`layer-note-input-${key}`} />
                                    <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" onClick={() => handleAddLayerNote(key)}
                                      disabled={addingLayerNote === key || !(layerNoteInput[key]?.trim())} data-testid={`layer-note-add-${key}`}><Plus className="w-3 h-3" /></Button>
                                    <Button size="sm" variant="ghost" className="h-6 px-1 text-xs text-amber-500"
                                      onClick={() => setMilestoneForm(p => ({ ...p, [key]: p[key] ? null : { title: "", start_date: "", end_date: "", notes: "" } }))}
                                      data-testid={`add-milestone-btn-${key}`}><Milestone className="w-3 h-3" /></Button>
                                  </div>

                                  {/* Milestone form */}
                                  {mForm && (
                                    <div className="mt-1 ml-4 p-2 bg-amber-50 rounded border border-amber-200 space-y-1">
                                      <Input value={mForm.title} onChange={(e) => setMilestoneForm(p => ({ ...p, [key]: { ...p[key], title: e.target.value } }))}
                                        placeholder="Milestone title" className="h-6 text-xs" data-testid={`milestone-title-${key}`} />
                                      <div className="flex items-center gap-2">
                                        <Input type="date" value={mForm.start_date || ""} onChange={(e) => setMilestoneForm(p => ({ ...p, [key]: { ...p[key], start_date: e.target.value } }))} className="h-6 text-xs w-[120px]" />
                                        <span className="text-xs text-slate-400">to</span>
                                        <Input type="date" value={mForm.end_date || ""} onChange={(e) => setMilestoneForm(p => ({ ...p, [key]: { ...p[key], end_date: e.target.value } }))} className="h-6 text-xs w-[120px]" />
                                      </div>
                                      <Input value={mForm.notes || ""} onChange={(e) => setMilestoneForm(p => ({ ...p, [key]: { ...p[key], notes: e.target.value } }))}
                                        placeholder="Notes" className="h-6 text-xs" />
                                      <div className="flex gap-1">
                                        <Button size="sm" className="h-6 text-xs px-2" onClick={() => handleAddMilestone(key)}>Add</Button>
                                        <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => setMilestoneForm(p => ({ ...p, [key]: null }))}>Cancel</Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* ===== NOTES TAB ===== */}
          <TabsContent value="notes" className="flex-1 overflow-hidden flex flex-col">
            <div className="flex gap-2 mb-3 flex-shrink-0">
              <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a card-level note..." className="flex-1"
                onKeyDown={(e) => e.key === "Enter" && !addingNote && handleAddNote()} disabled={addingNote} data-testid="note-input" />
              <Button onClick={handleAddNote} disabled={!newNote.trim() || addingNote} data-testid="add-note-btn">
                {addingNote ? "..." : <><Plus className="w-4 h-4 mr-1" /> Add</>}
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="space-y-2 pr-4">
                {historyNotes.slice().reverse().map((note, i) => (
                  <div key={note.id || i} className="p-2 bg-slate-50 rounded text-sm">
                    <p className="text-slate-700">{note.note}</p>
                    <p className="text-xs text-slate-400 mt-1">{note.user} | {new Date(note.timestamp).toLocaleString()}</p>
                  </div>
                ))}
                {historyNotes.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No notes yet</p>}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* ===== FILES TAB ===== */}
          <TabsContent value="files" className="flex-1 overflow-hidden flex flex-col">
            <div className="flex gap-2 mb-3 flex-shrink-0">
              <div className="relative flex-1">
                <input type="file" onChange={handleFileUpload} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="file-upload-input" />
                <Button variant="outline" className="w-full" data-testid="file-upload-btn"><Upload className="w-4 h-4 mr-2" /> Upload File</Button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-3">Max 10MB. Auto-named: [Vertical]-[SKU]-[Project]-[date].[ext]</p>
            <ScrollArea className="flex-1">
              <div className="space-y-2 pr-4">
                {attachments.map((file, i) => (
                  <div key={file.id || i} className="flex items-center justify-between p-2 bg-slate-50 rounded hover:bg-slate-100">
                    <a href={`${API}/plm/files/${file.id}/download?token=${token}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 flex-1" data-testid={`file-download-${file.id}`}>
                      <FileText className="w-4 h-4 text-slate-500" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-slate-700 truncate block">{file.filename}</span>
                        <span className="text-xs text-slate-400">{file.original_filename ? `(${file.original_filename}) ` : ''}{file.uploaded_by} | {new Date(file.uploaded_at).toLocaleDateString()}</span>
                      </div>
                    </a>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => { if (window.confirm("Delete?")) deleteFile(file.id); }} data-testid={`file-delete-${file.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
                {attachments.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No files uploaded</p>}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
